import React, { useState, useEffect } from 'react';
import { Box, Grid, CircularProgress, Typography } from '@mui/material';
import KpiPanel from './KpiPanel';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { api } from '../../utils/api';

// Chart components for each time period
function DailyActiveUsersChart({ data }) {
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis 
          dataKey="day" 
          tickFormatter={formatDate}
        />
        <YAxis />
        <Tooltip
          labelFormatter={formatDate}
          formatter={(value) => [value, 'Active Users']}
        />
        <Legend />
        <Line 
          type="monotone" 
          dataKey="count" 
          stroke="#8884d8" 
          name="Daily Active Users"
          strokeWidth={2}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

function MonthlyActiveUsersChart({ data }) {
  const formatMonth = (dateStr) => {
    const date = new Date(dateStr);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis 
          dataKey="month" 
          tickFormatter={formatMonth}
        />
        <YAxis />
        <Tooltip
          labelFormatter={formatMonth}
          formatter={(value) => [value, 'Active Users']}
        />
        <Legend />
        <Line 
          type="monotone" 
          dataKey="count" 
          stroke="#82ca9d" 
          name="Monthly Active Users"
          strokeWidth={2}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

function YearlyActiveUsersChart({ data }) {
  const formatYear = (dateStr) => {
    const year = new Date(dateStr).getFullYear();
    return year.toString();
  };

  return (
    <ResponsiveContainer width="100%" height={300}>
      <LineChart data={data} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis 
          dataKey="year" 
          tickFormatter={formatYear}
        />
        <YAxis />
        <Tooltip
          labelFormatter={formatYear}
          formatter={(value) => [value, 'Active Users']}
        />
        <Legend />
        <Line 
          type="monotone" 
          dataKey="count" 
          stroke="#ffc658" 
          name="Yearly Active Users"
          strokeWidth={2}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

function ActiveUsers() {
  const [loading, setLoading] = useState(true);
  const [dailyData, setDailyData] = useState({ currentDay: null, trend: [] });
  const [monthlyData, setMonthlyData] = useState({ currentMonth: null, trend: [] });
  const [yearlyData, setYearlyData] = useState({ years: [] });

  useEffect(() => {
    fetchActiveUsersData();
  }, []);

  const fetchActiveUsersData = async () => {
    try {
      setLoading(true);
      
      // Fetch all three endpoints in parallel
      const [daily, monthly, yearly] = await Promise.all([
        api.get('/users/daily-active-users'),
        api.get('/users/monthly-active-users'),
        api.get('/users/yearly-active-users')
      ]);

      setDailyData(daily);
      setMonthlyData(monthly);
      setYearlyData(yearly);
    } catch (error) {
      console.error('Error fetching active users data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Get current values for KPI boxes
  const currentDailyCount = dailyData.currentDay?.count || 0;
  const currentMonthlyCount = monthlyData.currentMonth?.count || 0;
  const currentYearlyCount = yearlyData.years.length > 0 ? yearlyData.years[yearlyData.years.length - 1].count : 0;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Active Users
      </Typography>
      
      {/* KPI Boxes Row */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <KpiPanel 
            title="Today's Active Users" 
            value={currentDailyCount.toLocaleString()}
            sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <KpiPanel 
            title="Month To Date Active Users" 
            value={currentMonthlyCount.toLocaleString()}
            sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <KpiPanel 
            title="Year To Date Active Users" 
            value={currentYearlyCount.toLocaleString()}
            sx={{ bgcolor: 'primary.light', color: 'primary.contrastText' }}
          />
        </Grid>
      </Grid>

      {/* Daily Active Users Chart */}
      <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
        <Typography variant="h6" gutterBottom>
          Daily Active Users Trend (Last 90 Days)
        </Typography>
        <DailyActiveUsersChart data={dailyData.trend} />
      </Box>

      {/* Monthly Active Users Chart */}
      <Box sx={{ mb: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
        <Typography variant="h6" gutterBottom>
          Monthly Active Users Trend
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          * Active User counts are not available prior to May 2025
        </Typography>
        <MonthlyActiveUsersChart data={monthlyData.trend} />
      </Box>

      {/* Yearly Active Users Chart */}
      <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
        <Typography variant="h6" gutterBottom>
          Yearly Active Users Trend
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          * Active User counts are not available prior to May 2025
        </Typography>
        <YearlyActiveUsersChart data={yearlyData.years} />
      </Box>
    </Box>
  );
}

export default ActiveUsers;
